import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageDetect } from '@/hooks/useLanguageDetect';
import { supabase } from '@/lib/supabase';

interface Article {
  id: string;
  title: string;
  title_zh_tw: string;
  slug: string;
  summary: string;
  summary_zh_tw: string;
  image_url: string;
  category_id: string;
  tags: string[];
  is_published: boolean;
  created_at: string;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function ArticleList() {
  const { t } = useTranslation();
  const { language } = useLanguageDetect();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchArticles() {
      try {
        const { data, error: supabaseError } = await supabase
          .from('articles')
          .select('id, title, title_zh_tw, slug, summary, summary_zh_tw, image_url, category_id, tags, is_published, created_at')
          .eq('is_published', true)
          .order('created_at', { ascending: false });

        if (supabaseError) throw supabaseError;
        setArticles(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    }

    fetchArticles();
  }, []);

  if (error) {
    return (
      <section id="latest-articles" className="w-full py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <p className="text-foreground-500">{t('loadError')}</p>
          <button
            onClick={() => { setError(null); setLoading(true); window.location.reload(); }}
            className="mt-4 text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer"
          >
            {t('retry') || '重新加载'}
          </button>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section id="latest-articles" className="w-full py-14 md:py-20 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <div className="w-40 h-4 bg-background-200 rounded-full mx-auto mb-3 animate-pulse" />
            <div className="w-64 h-8 bg-background-200 rounded-full mx-auto mb-3 animate-pulse" />
            <div className="w-80 h-4 bg-background-200 rounded-full mx-auto animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-background-50 rounded-lg overflow-hidden border border-background-200">
                <div className="w-full h-48 bg-background-200 animate-pulse" />
                <div className="p-5">
                  <div className="w-full h-5 bg-background-200 rounded animate-pulse mb-3" />
                  <div className="w-full h-4 bg-background-200 rounded animate-pulse mb-2" />
                  <div className="w-2/3 h-4 bg-background-200 rounded animate-pulse mb-3" />
                  <div className="flex justify-between pt-3 border-t border-background-200">
                    <div className="w-20 h-3 bg-background-200 rounded animate-pulse" />
                    <div className="w-12 h-3 bg-background-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="latest-articles" className="w-full py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs text-foreground-400 font-medium tracking-widest uppercase mb-3">
            {t('latestArticles')}
          </p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
            {t('latestArticles')}
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            周团长的健康知识分享，帮助你建立正确的营养补充观念
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors flex flex-col"
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  alt={language === 'zh-Hans' ? article.title : article.title_zh_tw}
                  title={language === 'zh-Hans' ? article.title : article.title_zh_tw}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  src={article.image_url}
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-3 line-clamp-2 leading-snug">
                  {language === 'zh-Hans' ? article.title : article.title_zh_tw}
                </h3>
                <div className="flex-1">
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3">
                    {language === 'zh-Hans' ? article.summary : article.summary_zh_tw}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200">
                  <p className="text-xs text-foreground-400">
                    {formatDate(article.created_at)}
                  </p>
                  <span className="text-xs text-primary-600 font-medium group-hover:text-primary-700 transition-colors flex items-center gap-0.5">
                    {t('readMore')}
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}